import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Paper,
  Divider,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Alert,
  Fade,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  People as PeopleIcon,
  Lock as LockIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon,
  AccessTime as TimeIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useRole } from '../../hooks/useRole';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import PageLayout from '../../components/Layout/PageLayout';
import SystemConfigDialog from '../../components/Dialogs/SystemConfigDialog';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const {
    isAdmin,
    isAdminOrManager,
    canCreateUser,
    canEditData,
    canViewData,
    roleDisplayName
  } = useRole();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // 系統配置對話框狀態
  const [systemConfigDialog, setSystemConfigDialog] = useState(false);

  // 設置功能卡片數據
  const settingsCards = [
    {
      id: 'create-user',
      title: '創建用戶',
      description: '添加新的系統用戶帳戶',
      icon: <PersonAddIcon sx={{ fontSize: 40 }} />,
      color: '#1976d2',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      route: '/user-management',
      action: 'create',
      requiredPermission: 'user.create',
      available: canCreateUser
    },
    {
      id: 'user-management',
      title: '用戶管理',
      description: '編輯、刪除和管理現有用戶',
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: '#2e7d32',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      route: '/user-management',
      action: 'manage',
      requiredPermission: 'system.admin',
      available: isAdminOrManager
    },
    {
      id: 'change-password',
      title: '更改密碼',
      description: '修改您的登入密碼',
      icon: <LockIcon sx={{ fontSize: 40 }} />,
      color: '#ed6c02',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      route: '/change-password',
      action: 'password',
      requiredPermission: 'user.edit',
      available: true // 所有用戶都可以更改自己的密碼
    },
    {
      id: 'system-config',
      title: '系統配置',
      description: '編輯系統參數和配置設定',
      icon: <TuneIcon sx={{ fontSize: 40 }} />,
      color: '#9c27b0',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      action: 'system-config',
      requiredPermission: 'system.admin',
      available: isAdminOrManager
    }
  ];

  const handleCardClick = (card: typeof settingsCards[0]) => {
    switch (card.action) {
      case 'system-config':
        if (isAdminOrManager) {
          setSystemConfigDialog(true);
        }
        break;
      case 'create':
        if (isAdminOrManager) {
          card.route && navigate(card.route);
        }
        break;
      case 'manage':
        if (isAdminOrManager) {
          card.route && navigate(card.route);
        }
        break;
      default:
        card.route && navigate(card.route);
    }
    // if (card.action === 'system-config' && isAdminOrManager) {
    //   setSystemConfigDialog(true);
    // } else if (card.route) {
    //   navigate(card.route);
    // }
  };

  return (
    <PageLayout title="系統設定">
      <Container maxWidth="xl">
        {/* 頁面標題和描述 */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            {t('pages.settings.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            管理系統用戶、權限和配置設定
          </Typography>

          {/* 當前用戶信息 */}
          <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                {user?.nickName?.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h6">{user?.nickName}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={roleDisplayName}
                    color="primary"
                    size="small"
                    icon={<AdminIcon />}
                  />
                  <Typography variant="body2" color="text.secondary">
                    系統管理員
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* 主要功能卡片 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {settingsCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={card.id}>
              <Fade in timeout={300 + index * 100}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: card.available ? 'pointer' : 'not-allowed',
                    opacity: card.available ? 1 : 0.6,
                    transition: 'all 0.3s ease',
                    '&:hover': card.available ? {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8]
                    } : {},
                    background: card.gradient,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onClick={() => handleCardClick(card)}
                >
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {card.icon}
                      <Box sx={{ ml: 2 }}>
                        <Typography variant="h6" component="h3">
                          {card.title}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {card.description}
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      size="small"
                      sx={{
                        color: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                      variant="outlined"
                      disabled={!card.available}
                    >
                      {'進入'}
                    </Button>
                  </CardActions>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* 快速操作區域 */}
        <Grid container spacing={3}>
          {/* 權限概覽 */}
          <Grid item xs={12} md={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon color="primary" />
                權限概覽
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                您當前的系統權限
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <AdminIcon sx={{ fontSize: 40, color: isAdmin ? 'success.main' : 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      管理員權限
                    </Typography>
                    <Chip
                      label={isAdmin ? '是' : '否'}
                      color={isAdmin ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <PeopleIcon sx={{ fontSize: 40, color: canViewData ? 'success.main' : 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      用戶管理
                    </Typography>
                    <Chip
                      label={isAdminOrManager ? '有權限' : '無權限'}
                      color={isAdminOrManager ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <PersonAddIcon sx={{ fontSize: 40, color: canCreateUser ? 'success.main' : 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      創建用戶
                    </Typography>
                    <Chip
                      label={canCreateUser ? '有權限' : '無權限'}
                      color={canCreateUser ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <EditIcon sx={{ fontSize: 40, color: canEditData ? 'success.main' : 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      編輯權限
                    </Typography>
                    <Chip
                      label={canEditData ? '有權限' : '無權限'}
                      color={canEditData ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* 功能說明 */}
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            功能說明
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                1. 創建用戶
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                添加新的系統用戶，設置角色和權限。只有管理員和經理可以創建新用戶。
              </Typography>

              <Typography variant="subtitle2" color="primary" gutterBottom>
                2. 用戶管理
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                編輯現有用戶信息、更改角色、啟用/停用帳戶。
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                3. 更改密碼
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                修改您的登入密碼。所有用戶都可以更改自己的密碼。
              </Typography>

              <Typography variant="subtitle2" color="primary" gutterBottom>
                4. 時間範圍設定
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                配置系統時間範圍、時區和時間相關設定。此功能即將推出。
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* 系統配置對話框 */}
      <SystemConfigDialog
        open={systemConfigDialog}
        onClose={() => setSystemConfigDialog(false)}
        onSave={(config) => {
          console.log('系統配置已更新:', config);
          setSystemConfigDialog(false);
        }}
      />
    </PageLayout>
  );
};

export default SettingsPage;
